import React from 'react';
import PropTypes from 'prop-types';
import { useMutation, gql } from '@apollo/client';
import Avatar from '../StudentPlatform/shared/Avatar';
import {
  XMarkIcon,
  AcademicCapIcon,
  MapPinIcon,
  CalendarIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  LinkIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const CREATE_CHAT = gql`
  mutation CreateChat($userName: String!) {
    createChat(userName: $userName) {
      id
    }
  }
`;

const StudentProfileModal = ({ student, currentUser, onClose, isMessaging }) => {
  const [createChat, { loading }] = useMutation(CREATE_CHAT);
  
  const displayName = student.user?.allowed_name || student.user?.name || 'Student';
  const avatarUrl = student.avatar_url; // Student's custom uploaded avatar

  const handleMessage = async () => {
    try {
      // If already messaging, just navigate to messages page
      if (isMessaging) {
        window.location.href = '/succession/messages';
        return;
      }

      // Create new chat
      const result = await createChat({
        variables: {
          userName: student.user?.user_name,
        },
      });

      if (result.data?.createChat?.id) {
        const conversationId = result.data.createChat.id;
        window.location.href = `/succession/messages?conversation_id=${conversationId}`;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      alert('Unable to send message. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-succession-bg-secondary rounded-lg shadow-succession-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-succession-border">
          {/* Header */}
          <div className="sticky top-0 bg-succession-bg-secondary border-b border-succession-border px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-succession-text-primary">Student Profile</h2>
              {isMessaging && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-700 gap-1">
                  <ChatBubbleLeftRightIcon className="h-3 w-3" />
                  <span>In conversation</span>
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-primary"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Profile Header */}
            <div className="flex items-start gap-6 mb-6 pb-6 border-b border-succession-border">
              <Avatar
                src={avatarUrl}
                name={displayName}
                size="xl"
                className="border-2 border-succession-border flex-shrink-0"
              />
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-succession-text-primary mb-2">
                  {displayName}
                </h3>
                {student.school && (
                  <p className="text-lg text-succession-text-secondary mb-1">
                    {student.school}
                  </p>
                )}
                {student.graduation_year && (
                  <p className="text-succession-text-secondary mb-4">
                    Class of {student.graduation_year}
                  </p>
                )}
                <div>
                  <button
                    onClick={handleMessage}
                    disabled={loading}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                      loading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-succession-primary text-white hover:bg-succession-primary-dark'
                    }`}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {loading ? 'Starting conversation...' : isMessaging ? 'Continue Conversation' : 'Send Message'}
                  </button>
                  {isMessaging && (
                    <p className="text-xs text-succession-text-muted mt-2">
                      You're already in conversation with this student.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-succession-text-primary mb-4">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {student.user?.email && (
                  <div className="flex items-start gap-3">
                    <EnvelopeIcon className="h-5 w-5 text-succession-text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">Email</p>
                      <p className="text-sm font-medium text-succession-text-primary break-all">{student.user.email}</p>
                    </div>
                  </div>
                )}
                {student.phone && (
                  <div className="flex items-start gap-3">
                    <PhoneIcon className="h-5 w-5 text-succession-text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">Phone</p>
                      <p className="text-sm font-medium text-succession-text-primary">{student.phone}</p>
                    </div>
                  </div>
                )}
                {(student.city || student.state) && (
                  <div className="flex items-start gap-3">
                    <MapPinIcon className="h-5 w-5 text-succession-text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">Location</p>
                      <p className="text-sm font-medium text-succession-text-primary">
                        {[student.city, student.state].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-succession-text-primary mb-4 flex items-center gap-2">
                <AcademicCapIcon className="h-5 w-5" />
                Education
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {student.school && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">School</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.school}</p>
                  </div>
                )}
                {student.degree_program && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">Degree Program</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.degree_program}</p>
                  </div>
                )}
                {student.graduation_year && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">Graduation Year</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.graduation_year}</p>
                  </div>
                )}
                {student.employment_type && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">Seeking</p>
                    <p className="text-sm font-medium text-succession-text-primary">
                      {student.employment_type === 'internship' ? 'Internship' : 'Full-Time Position'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Career Interests */}
            {(student.desired_location || student.employment_type || student.availability) && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-succession-text-primary mb-4 flex items-center gap-2">
                  <BriefcaseIcon className="h-5 w-5" />
                  Career Interests
                </h4>
                <div className="space-y-3">
                  {student.desired_location && (
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">Preferred Location</p>
                      <p className="text-sm font-medium text-succession-text-primary">{student.desired_location}</p>
                    </div>
                  )}
                  {student.availability && Object.keys(student.availability).length > 0 && (
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">Availability</p>
                      <p className="text-sm font-medium text-succession-text-primary">
                        {Object.entries(student.availability)
                          .filter(([_, value]) => value)
                          .map(([key, _]) => key.charAt(0).toUpperCase() + key.slice(1))
                          .join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Why Interested */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-succession-text-primary mb-4">
                Why I'm Interested in Financial Services
              </h4>
              {student.why_interested ? (
                <p className="text-sm text-succession-text-secondary leading-relaxed">
                  {student.why_interested}
                </p>
              ) : (
                <p className="text-sm text-succession-text-muted italic">
                  Not provided yet
                </p>
              )}
            </div>

            {/* Certifications */}
            {student.certifications && student.certifications.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-succession-text-primary mb-4">Certifications</h4>
                <div className="flex flex-wrap gap-2">
                  {student.certifications.map((cert, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-md border border-succession-border text-sm text-succession-text-primary bg-succession-bg-primary"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Info */}
            {student.family_in_business !== null && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-succession-text-primary mb-4">Additional Information</h4>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-succession-text-secondary">Family in financial business:</span>
                  <span className="text-sm font-medium text-succession-text-primary">
                    {student.family_in_business ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            )}

            {/* Resume & Video */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Resume */}
              <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <DocumentTextIcon className="h-6 w-6 text-succession-text-secondary" />
                  <h4 className="font-semibold text-succession-text-primary">Resume</h4>
                </div>
                {student.resume_url ? (
                  <a
                    href={student.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-succession-primary hover:underline"
                  >
                    View Resume →
                  </a>
                ) : (
                  <p className="text-sm text-succession-text-muted">No resume uploaded yet</p>
                )}
              </div>

              {/* Video Introduction */}
              <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <VideoCameraIcon className="h-6 w-6 text-succession-text-secondary" />
                  <h4 className="font-semibold text-succession-text-primary">Video Introduction</h4>
                </div>
                {student.video_url ? (
                  <a
                    href={student.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-succession-primary hover:underline"
                  >
                    Watch Video →
                  </a>
                ) : (
                  <p className="text-sm text-succession-text-muted">No video uploaded yet</p>
                )}
              </div>
            </div>

            {/* LinkedIn */}
            {student.linkedin_url && (
              <div className="mb-6">
                <a
                  href={student.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-primary hover:bg-succession-hover-bg"
                >
                  <LinkIcon className="h-5 w-5" />
                  View LinkedIn Profile
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-succession-bg-secondary border-t border-succession-border px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-succession-border text-succession-text-primary hover:bg-succession-bg-primary"
            >
              Close
            </button>
            <button
              onClick={handleMessage}
              disabled={loading}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-succession-primary text-white hover:bg-succession-primary-dark'
              }`}
            >
              {loading ? 'Starting...' : isMessaging ? 'Continue Conversation' : 'Send Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StudentProfileModal.propTypes = {
  student: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  isMessaging: PropTypes.bool,
};

export default StudentProfileModal;

